import {
  YES_NO,
  applicantSection,
  businessSection,
  feeProofAttachment,
  otherAttachments,
  proxyAttachments,
  replySection,
  representationSection,
} from "modules/ebiForms/common";
import { EbiCondition, EEbiFormCategory, EEbiFormOffice, IEbiFormDefinition } from "modules/ebiForms/typings";

/**
 * G8. Zgłoszenie instalacji, z której emisja nie wymaga pozwolenia — art. 152 ustawy — Prawo ochrony środowiska
 * (treść: ust. 2 pkt 1–7 i 9, źródła spalania 1–50 MW: ust. 2b–2c, informacje o zmianach: ust. 6 pkt 1 i ust. 7 —
 * sprawdzone w tekście jednolitym Dz.U. 2025 poz. 647); opłata 120 zł: część I pkt 13 załącznika do ustawy o opłacie
 * skarbowej. Brak wzoru ogólnopolskiego; układ jak wzór Starostwa Powiatowego w Koszalinie
 * (https://bip.powiat.koszalin.pl/uploads/files/Ochrona%20%C5%9Brodowiska/wniosek%20-zg%C5%82oszenie%20instalacji%20(1).docx,
 * pobrany 22.10.2026) — punkty art. 152 ust. 2 jako pola formularza. Informacja o rezygnacji, zakończeniu eksploatacji
 * lub zmianie danych (art. 152 ust. 6 pkt 1) to drugi wariant pisma. Formularz PEM z rozporządzenia MŚ z 2.07.2010 r.
 * — załącznik nieobowiązkowy (do potwierdzenia z wydziałem).
 */
const NOTIFICATION: EbiCondition = { field: "rodzajPisma", equals: "zgloszenie" };
const INFORMATION: EbiCondition = { field: "rodzajPisma", equals: "informacja" };
const BUSINESS: EbiCondition = { field: "prowadzacyInstalacje", equals: "przedsiebiorca" };
const EMF: EbiCondition = { all: [NOTIFICATION, { field: "pem", equals: "tak" }] };
const COMBUSTION: EbiCondition = { all: [NOTIFICATION, { field: "zrodloSpalania", equals: "tak" }] };
const START_DATE_UNKNOWN: EbiCondition = { all: [COMBUSTION, { field: "dataOddaniaZnana", equals: "nie" }] };

const G8: IEbiFormDefinition = {
  code: "G8",
  version: 1,
  title: "Zgłoszenie instalacji, z której emisja nie wymaga pozwolenia",
  category: EEbiFormCategory.Environment,
  office: EEbiFormOffice.County,
  documentType: "Pismo",
  description: "Zgłoszenie instalacji mogącej negatywnie oddziaływać na środowisko (art. 152 POŚ) albo informacja o jej zmianie lub zakończeniu eksploatacji.",
  keywords: ["zgłoszenie instalacji", "emisja", "art. 152", "ochrona środowiska", "pola elektromagnetyczne", "stacja bazowa", "kotłownia"],
  organ: "Starosta (art. 378 ust. 1 POŚ)",
  legalBasis: "art. 152 ustawy z dnia 27 kwietnia 2001 r. — Prawo ochrony środowiska",
  request:
    "Na podstawie art. 152 ustawy z dnia 27 kwietnia 2001 r. — Prawo ochrony środowiska zgłaszam instalację, z której emisja nie wymaga pozwolenia, albo przedkładam informację dotyczącą zgłoszonej instalacji — zgodnie z treścią pisma.",
  feeLabel: "120 zł",
  fee: "120 zł opłaty skarbowej za przyjęcie zgłoszenia instalacji; pełnomocnictwo — 17 zł.",
  deadlineLabel: "30 dni na sprzeciw",
  deadline: "Organ może wnieść sprzeciw w drodze decyzji w terminie 30 dni od doręczenia zgłoszenia (art. 152 ust. 4 POŚ).",
  variantField: "rodzajPisma",
  variants: {
    zgloszenie: { title: "Zgłoszenie instalacji, z której emisja nie wymaga pozwolenia" },
    informacja: { title: "Informacja o rezygnacji, zakończeniu eksploatacji lub zmianie danych zgłoszonej instalacji" },
  },
  sections: [
    {
      code: "rodzaj",
      title: "Rodzaj pisma",
      fields: [
        {
          code: "rodzajPisma",
          type: "radio",
          label: "Składam",
          required: true,
          defaultValue: "zgloszenie",
          options: [
            { value: "zgloszenie", label: "zgłoszenie instalacji (art. 152 ust. 1 POŚ)" },
            {
              value: "informacja",
              label: "informację o rezygnacji z rozpoczęcia eksploatacji, zakończeniu eksploatacji albo zmianie danych zawartych w zgłoszeniu (art. 152 ust. 6 pkt 1 POŚ)",
            },
          ],
        },
        {
          code: "trybZgloszenia",
          type: "radio",
          label: "Zgłoszenie dotyczy instalacji",
          required: true,
          visibleWhen: NOTIFICATION,
          options: [
            { value: "nowa", label: "nowo zbudowanej lub zmienionej w sposób istotny — przed rozpoczęciem eksploatacji (art. 152 ust. 3)" },
            { value: "eksploatowana", label: "już eksploatowanej, która została objęta obowiązkiem zgłoszenia (art. 152 ust. 5)" },
            {
              value: "ponowne",
              label: "zgłaszanej ponownie — po istotnej zmianie albo po nierozpoczęciu eksploatacji w ciągu 12 miesięcy (art. 152 ust. 6 pkt 2)",
            },
          ],
        },
        {
          code: "infoWlasciwosc",
          type: "info",
          label:
            "Zgłoszenie przyjmuje starosta. Zgłoszenie osoby fizycznej niebędącej przedsiębiorcą (zwykłe korzystanie ze środowiska) przyjmuje wójt, burmistrz lub prezydent miasta (art. 378 ust. 3 pkt 3 POŚ), a w sprawach z art. 378 ust. 2a POŚ — marszałek województwa.",
        },
      ],
    },
    applicantSection("Zgłaszający"),
    representationSection(),
    {
      code: "prowadzacy",
      title: "Prowadzący instalację",
      fields: [
        {
          code: "prowadzacyInstalacje",
          type: "radio",
          label: "Instalację prowadzi",
          required: true,
          options: [
            { value: "zglaszajacy", label: "zgłaszający (albo mocodawca, gdy działa pełnomocnik)" },
            { value: "przedsiebiorca", label: "przedsiębiorca, w imieniu którego działam (właściciel firmy albo osoba reprezentująca)" },
          ],
        },
        { code: "nazwaInstalacji", type: "text", label: "Instalacja (nazwa i rodzaj)", required: true, maxLength: 300, placeholder: "np. kotłownia gazowa, lakiernia, stacja bazowa telefonii komórkowej" },
        { code: "adresZakladu", type: "text", label: "Adres zakładu, na którego terenie prowadzona jest eksploatacja instalacji", required: true, maxLength: 300 },
      ],
    },
    businessSection("Dane przedsiębiorcy prowadzącego instalację", BUSINESS),
    {
      code: "tresc",
      title: "Treść zgłoszenia (art. 152 ust. 2 POŚ)",
      visibleWhen: NOTIFICATION,
      fields: [
        {
          code: "rodzajDzialalnosci",
          type: "textarea",
          label: "Rodzaj i zakres prowadzonej działalności, w tym wielkość produkcji lub wielkość świadczonych usług",
          required: true,
          maxLength: 3000,
        },
        { code: "czasFunkcjonowania", type: "text", label: "Czas funkcjonowania instalacji (dni tygodnia i godziny)", required: true, maxLength: 300 },
        { code: "emisja", type: "textarea", label: "Wielkość i rodzaj emisji", required: true, maxLength: 5000 },
        { code: "metodyOgraniczania", type: "textarea", label: "Opis stosowanych metod ograniczania wielkości emisji", required: true, maxLength: 5000 },
        {
          code: "zgodnoscEmisji",
          type: "radio",
          label: "Czy stopień ograniczania wielkości emisji jest zgodny z obowiązującymi przepisami?",
          required: true,
          options: YES_NO,
        },
        { code: "zgodnoscWyjasnienie", type: "textarea", label: "Wyjaśnienie (nieobowiązkowo)", maxLength: 2000 },
        {
          code: "pem",
          type: "radio",
          label:
            "Czy instalacja emituje pola elektromagnetyczne i podlega pomiarom z art. 122a ust. 1 POŚ (stacje lub napowietrzne linie elektroenergetyczne o napięciu co najmniej 110 kV, instalacje radiokomunikacyjne, radionawigacyjne lub radiolokacyjne o równoważnej mocy promieniowanej izotropowo co najmniej 15 W)?",
          required: true,
          options: YES_NO,
        },
        {
          code: "zrodloSpalania",
          type: "radio",
          label:
            "Czy częścią instalacji jest źródło spalania paliw o nominalnej mocy cieplnej nie mniejszej niż 1 MW i mniejszej niż 50 MW, dla którego standardy emisyjne określają przepisy wydane na podstawie art. 146 ust. 3 POŚ?",
          required: true,
          options: YES_NO,
        },
      ],
    },
    {
      code: "spalanie",
      title: "Źródło spalania paliw (art. 152 ust. 2b–2c POŚ)",
      visibleWhen: COMBUSTION,
      fields: [
        { code: "kodPkd", type: "text", label: "Sektor, w którym działa źródło spalania paliw lub zakład (kod PKD)", required: true, width: "half", maxLength: 20 },
        { code: "regonProwadzacego", type: "text", label: "Numer REGON prowadzącego instalację", required: true, width: "half", maxLength: 14 },
        {
          code: "mocCieplna",
          type: "number",
          label: "Nominalna moc cieplna źródła spalania paliw [MW]",
          required: true,
          width: "half",
          decimals: 2,
          min: 1,
          max: 49.99,
        },
        { code: "rodzajZrodla", type: "text", label: "Rodzaj źródła spalania paliw", required: true, width: "half", maxLength: 200 },
        { code: "paliwa", type: "textarea", label: "Rodzaj i przewidywany udział procentowy wykorzystywanych paliw", required: true, maxLength: 1000 },
        { code: "standardyEmisyjne", type: "textarea", label: "Standardy emisyjne obowiązujące dane źródło spalania paliw", required: true, maxLength: 2000 },
        { code: "dataOddaniaZnana", type: "radio", label: "Czy data oddania źródła spalania paliw do użytkowania jest znana?", required: true, options: YES_NO },
        {
          code: "dataOddania",
          type: "date",
          label: "Data oddania źródła spalania paliw do użytkowania",
          required: true,
          visibleWhen: { field: "dataOddaniaZnana", equals: "tak" },
          width: "half",
        },
        { code: "czasUzytkowania", type: "number", label: "Przewidywany czas użytkowania źródła w ciągu roku [h]", required: true, width: "half", min: 0, max: 8784 },
        {
          code: "obciazenie",
          type: "number",
          label: "Przewidywane średnie obciążenie podczas użytkowania [%]",
          required: true,
          width: "half",
          decimals: 2,
          min: 0,
          max: 100,
        },
        {
          code: "czas500",
          type: "radio",
          label: "Czy czas użytkowania źródła, liczony jako średnia krocząca, nie przekracza limitu z art. 152 ust. 2c POŚ (500 godzin w ciągu roku)?",
          required: true,
          options: YES_NO,
        },
        {
          code: "oswiadczenieCzasu",
          type: "declaration",
          label:
            "Oświadczam, że czas użytkowania źródła spalania paliw, liczony jako średnia krocząca, nie przekracza czasu użytkowania określonego w art. 152 ust. 2c ustawy — Prawo ochrony środowiska.",
          required: true,
          visibleWhen: { field: "czas500", equals: "tak" },
        },
      ],
    },
    {
      code: "informacja",
      title: "Informacja dotycząca zgłoszonej instalacji (art. 152 ust. 6 pkt 1 POŚ)",
      visibleWhen: INFORMATION,
      fields: [
        {
          code: "rodzajInformacji",
          type: "radio",
          label: "Informuję o",
          required: true,
          options: [
            { value: "rezygnacja", label: "rezygnacji z rozpoczęcia eksploatacji instalacji" },
            { value: "zakonczenie", label: "zakończeniu eksploatacji instalacji" },
            { value: "zmiana", label: "zmianie danych lub informacji zawartych w zgłoszeniu (art. 152 ust. 2 i 2b) albo objętych oświadczeniem (ust. 2c)" },
          ],
        },
        { code: "dataZdarzenia", type: "date", label: "Data rezygnacji, zakończenia eksploatacji albo zmiany", required: true, width: "half" },
        { code: "dotychczasoweZgloszenie", type: "text", label: "Data i znak dotychczasowego zgłoszenia (jeśli znane)", width: "half", maxLength: 200 },
        { code: "opisInformacji", type: "textarea", label: "Opis (np. zakres zmienionych danych)", required: true, maxLength: 5000 },
        {
          code: "infoTermin",
          type: "info",
          label: "Informację przedkłada się w terminie 14 dni od dnia rezygnacji, zakończenia eksploatacji albo zmiany (art. 152 ust. 7 POŚ).",
        },
      ],
    },
    {
      code: "oplata",
      title: "Opłata skarbowa",
      visibleWhen: NOTIFICATION,
      fields: [
        {
          code: "kwotaOplaty",
          type: "fee",
          label: "Opłata skarbowa za przyjęcie zgłoszenia",
          feeAmount: 120,
          hint: "Część I pkt 13 załącznika do ustawy o opłacie skarbowej — na rachunek urzędu gminy właściwego dla siedziby organu.",
        },
        {
          code: "infoSprzeciw",
          type: "info",
          label:
            "Eksploatację instalacji nowo zbudowanej lub zmienionej w sposób istotny można rozpocząć, jeżeli organ w terminie 30 dni od dnia doręczenia zgłoszenia nie wniesie sprzeciwu w drodze decyzji (art. 152 ust. 4 POŚ).",
        },
      ],
    },
    replySection(),
  ],
  attachments: [
    feeProofAttachment("Dowód zapłaty opłaty skarbowej (120 zł)", { visibleWhen: NOTIFICATION }),
    {
      code: "sprawozdaniePem",
      label: "Sprawozdanie z wykonanych pomiarów poziomów pól elektromagnetycznych (art. 152 ust. 2 pkt 9 POŚ)",
      visibleWhen: EMF,
      required: true,
    },
    {
      code: "formularzPem",
      label: "Formularz zgłoszenia instalacji wytwarzającej pola elektromagnetyczne",
      hint: "Wzór z załącznika do rozporządzenia Ministra Środowiska z dnia 2 lipca 2010 r. w sprawie rodzajów instalacji, których eksploatacja wymaga zgłoszenia — jeśli urząd go wymaga.",
      visibleWhen: EMF,
    },
    {
      code: "dowodDatyOddania",
      label: "Dowód, że użytkowanie źródła spalania paliw rozpoczęto przed dniem 20 grudnia 2018 r. albo po dniu 19 grudnia 2018 r. (art. 152 ust. 2b pkt 7 POŚ)",
      visibleWhen: START_DATE_UNKNOWN,
      required: true,
    },
    {
      code: "dokumentacjaEmisji",
      label: "Opis technologii i urządzeń ograniczających emisję, obliczenia emisji, mapa z lokalizacją emitorów",
      hint: "Praktyka urzędów — nieobowiązkowo.",
      multiple: true,
      visibleWhen: NOTIFICATION,
    },
    ...proxyAttachments(),
    otherAttachments(),
  ],
};

export default G8;
